import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { ISchedulerData, ISchedulerTask, ISiteLocations, ITaskStockClaim } from '@interfaces/IScheduler';
import type { InventoryLevel } from '@interfaces/ILogistics';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { selectWorkingSite } from '@store/selectors/site.selectors';
import type { IOrderHandoff } from '../../../LogisticsPage/orderHandoff';
import { useTaskStock } from '../../stock/useTaskStock';
import { chosenIds, claimQuantity, elementsOf, unitKeyOfLabel, withChosen } from '../../stock/claimElements';
import { ownClaimFor } from '../../stock/claimIndex';
import { buildCheckoutHandoff, joinWritesFor, type ICheckoutTask, type IClaimDestination } from '../../stock/claimPrefill';
import { checkoutPlan, planHas } from '../../stock/checkoutPlan';
import { bookedLabel, cartStatus, pickedFloorNames, isFoldable } from '../../stock/cartItem';
import { floorsLabel } from '../../stock/takeoffByFloor';
import { BackToMaterialsButton } from './BackToMaterialsButton';
import { StockClaimRow } from './StockClaimRow';
import { StockClaimOutcome } from './StockClaimOutcome';
import { StockCartItem, StockCheckoutBar } from './StockCartItem';
import { StockCartTruck, type ICartEntry } from './StockCartTruck';
import { StockCheckoutReview } from './StockCheckoutReview';
import { StockElementPicker } from './StockElementPicker';
import { PackageIcon } from '@utils/icons';
import { EmptyStateTile } from '@components/ui/EmptyStateTile/EmptyStateTile';
import styles from './TaskPanelStockTab.module.css';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';

interface ITaskPanelStockTabProps {
  siteId: string;
  /** The loaded schedule — a delivery order's link to this task names it,
   *  since task ids repeat across schedules. */
  scheduleNumber: string;
  draft: ISchedulerTask;
  setDraft: Dispatch<SetStateAction<ISchedulerTask | null>>;
  data: ISchedulerData;
  /** The checkout's "Continue to order details": the drawer opens the
   *  delivery dialog with the new order's lines and the adds it also makes. */
  onCreateDelivery: (handoff: IOrderHandoff) => void;
  /** Persists the drawer's draft (the listed claims included) through the
   *  immediate-write channel — run right after the checkout's adds to orders
   *  on their way are fired, so an order never points at a claim that was
   *  never saved. The same callback the delivery dialog runs before its own
   *  write. */
  onBeforeJoin: () => void;
  /** Deep-link to a delivery on the Logistics page; absent where the drawer's
   *  host has no router. */
  onOpenDelivery?: (deliveryRef: string) => void;
  /** A tapped stock row raises its level — the drawer opens the stock record
   *  beside itself with it. */
  onOpenStock: (level: InventoryLevel) => void;
  /** Lifts "is any claim over its CAD ceiling" to the drawer footer, which
   *  disables Save on it — a callback rather than a DOM attribute (simpler
   *  than the tab reaching into the footer's own render). */
  onValidity: (blocked: boolean) => void;
  /** Lifts "the element list is open" to the drawer, which hides its own
   *  Save/Cancel footer while the list owns the bottom bar. Same pattern. */
  onPicking: (picking: boolean) => void;
}

/**
 * The tab's ONE way to add a material — the same dashed button in the empty
 * state and under an existing list, so the two can never drift. Alignment is
 * the caller's: centred in the empty tile, left of the list.
 */
function AddMaterialButton({ className, onClick }: { className: string | undefined; onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className={className} onClick={onClick}>
      {t('scheduler.panel.stock.addRow')}
    </button>
  );
}

/** A brand-new claim row — defaults to a CAD pick (the common case; "Not in
 *  the CAD" is a deliberate opt-out, not the starting point). */
function newClaim(floorIds: string[], cadVersionId: string | null): ITaskStockClaim {
  return {
    id: crypto.randomUUID(),
    materialName: '',
    fromCad: true,
    floorIds,
    quantity: 0,
    unit: '',
    cadVersionId,
    elements: [],
  };
}

/** The task's first assigned zone and its floor, by NAME (not id) — the
 *  checkout hand-off's destination reads `{floor} · {zone}` when it
 *  resolves; null when the task has no location or the zone is not in the
 *  catalog. */
function firstDestination(task: ISchedulerTask, catalog: ISiteLocations): IClaimDestination | null {
  const first = task.locations?.[0]?.locationId;
  for (const floor of catalog.floors) {
    const zone = floor.zones.find((z) => z.id === first);
    if (zone) return { floorName: floor.name, zoneName: zone.name };
  }
  return null;
}

/**
 * The task's materials as a cart (contract v14): each one the task needs goes
 * on the list without being ordered on the spot, and one checkout sorts them
 * — enough stock on site needs nothing, an order on its way takes the amount,
 * everything else goes on one new order.
 */
export function TaskPanelStockTab({ siteId, scheduleNumber, draft, setDraft, data, onCreateDelivery, onBeforeJoin, onOpenDelivery, onOpenStock, onValidity, onPicking }: ITaskPanelStockTabProps) {
  const { t, i18n } = useTranslation();
  const stock = useTaskStock(siteId, scheduleNumber, draft, data.tasks);
  const catalog = useSiteLocations(siteId);
  // Floor id → name, for the cart item's "picked from" floors.
  const floorNameOf = useMemo(() => {
    const names = new Map(catalog.floors.map((floor) => [floor.id, floor.name]));
    return (floorId: string) => names.get(floorId);
  }, [catalog]);
  const siteName = useSelector(selectWorkingSite)?.name ?? '';
  // `?? []` would allocate a fresh array every render when the task has no
  // claims yet, defeating the memo below — memoize the fallback too.
  const claims = useMemo(() => draft.stockClaims ?? [], [draft.stockClaims]);
  const destination = useMemo(() => firstDestination(draft, catalog), [draft, catalog]);

  const evaluations = useMemo(() => claims.map((c) => stock.evaluate(c)), [claims, stock]);
  // Two ways a claim blocks Save. `over` is the CAD ceiling (spec §5.2). `idle`
  // is a row the user started but never filled in — no material, no unit, or a
  // non-positive quantity: the backend's Zod schema rejects exactly that, and
  // it rejects the WHOLE schedule PUT, so a half-typed row would otherwise
  // lose every other edit in the drawer to a 400. Go back (dropping the row) or finish it.
  const blocked = evaluations.some((e) => e.outcome.kind === 'over' || e.outcome.kind === 'idle');
  // What the checkout would do with the list as it stands.
  const plan = useMemo(() => checkoutPlan(claims, evaluations, stock), [claims, evaluations, stock]);
  // The row being edited. A claim counts as "in progress" while it is being
  // worked on, not only while it is incomplete: the first selection already
  // gives it a quantity, and the row must not fold between choosing the
  // slabs and choosing the walls.
  const [editingId, setEditingId] = useState<string | null>(null);
  // The element list, when open: which claim and which of its types. While
  // set, the list takes the tab body's place — and so does the review.
  const [picking, setPicking] = useState<{ claimId: string; kind: string } | null>(null);
  const [reviewing, setReviewing] = useState(false);
  // A row folds to its cart item unless it is being edited — or cannot: a
  // blank row and one over its CAD ceiling block Save, so they keep the form.
  const foldedAt = (i: number) => {
    const { outcome } = evaluations[i]!;
    return claims[i]!.id !== editingId && isFoldable(outcome) ? outcome : null;
  };
  // The first form on screen carries the scope line under its back link; a
  // second one (two rows being built) need not repeat it.
  const firstExpanded = claims.findIndex((_, i) => foldedAt(i) === null);
  const anyExpanded = firstExpanded !== -1;
  /* A finished row can go to the checkout without going back first: the same
     bar the list carries sits under its form, so the two ways out are "back
     to the materials" above and "review the order" below (Niv, 2026-09-16). */
  const openRowDone = anyExpanded && isFoldable(evaluations[firstExpanded]!.outcome);
  /* What the truck carries: one entry per material the checkout still has
     something to do for, with the amount it would order. */
  const cart: ICartEntry[] = [
    ...plan.joins.map((join) => ({ claimId: join.claim.id, materialName: join.claim.materialName, amount: `${join.amount} ${join.order.line.unit}` })),
    ...plan.newLines.map((line) => ({ claimId: line.claim.id, materialName: line.claim.materialName, amount: `${line.need} ${line.claim.unit}` })),
    ...plan.blocked.map((item) => ({ claimId: item.claim.id, materialName: item.claim.materialName, amount: `${item.claim.quantity} ${item.claim.unit}` })),
  ];
  const truck =
    cart.length === 0 ? null : (
      <StockCartTruck
        entries={cart}
        onOpen={(claimId) => {
          setPicking(null);
          setEditingId(claimId);
        }}
        onReview={() => setReviewing(true)}
      />
    );

  // Reported to the footer via callback (ruling: no DOM attribute) — cleared
  // on unmount so switching away from the tab (or closing the drawer) never
  // leaves a stale block behind. The list's open state goes the same way.
  useEffect(() => { onValidity(blocked); }, [blocked, onValidity]);
  useEffect(() => () => onValidity(false), [onValidity]);
  useEffect(() => { onPicking(picking !== null); }, [picking, onPicking]);
  useEffect(() => () => onPicking(false), [onPicking]);

  function removeClaim(id: string) {
    setDraft((prev) => prev && { ...prev, stockClaims: (prev.stockClaims ?? []).filter((c) => c.id !== id) });
    // The back click put the cursor in the row it removed; a row that is
    // gone never blurs, so let go of it here or the add button stays hidden.
    setEditingId((current) => (current === id ? null : current));
  }

  function updateClaim(id: string, patch: Partial<ITaskStockClaim>) {
    // One claim per material per task (ruling 95): a pick naming a material
    // another row of this task already claims grows THAT claim. The row being
    // built goes, and the cursor moves to the existing one, which opens with
    // its types block and "Select…" links — never a second row.
    const existing = patch.materialName === undefined ? null : ownClaimFor(claims, patch.materialName, id);
    if (existing !== null) {
      removeClaim(id);
      setEditingId(existing.id);
      return;
    }
    /* Browsing another material must not take the first one's elements with
       it (Niv, 2026-09-16): a row that already names a material and holds
       something keeps what it has — it goes to the cart as it stands — and
       the pick starts a row of its own. Only an untouched row (no material
       yet, nothing chosen) is filled in place. */
    const row = claims.find((c) => c.id === id);
    if (patch.materialName !== undefined && row !== undefined && row.materialName !== '' && row.quantity > 0) {
      const fresh = newClaim(stock.floorIds, stock.cadVersionId);
      setDraft((prev) =>
        prev && { ...prev, stockClaims: [...(prev.stockClaims ?? []), { ...fresh, ...patch }] },
      );
      setEditingId(fresh.id);
      return;
    }
    setDraft((prev) =>
      prev && { ...prev, stockClaims: (prev.stockClaims ?? []).map((c) => (c.id === id ? { ...c, ...patch } : c)) },
    );
  }

  function addRow() {
    const claim = newClaim(stock.floorIds, stock.cadVersionId);
    setDraft((prev) => prev && { ...prev, stockClaims: [...(prev.stockClaims ?? []), claim] });
  }

  /** Back from the element list, with or without a new selection: to the
   *  row it was opened from, still being built. */
  function closePicker(claimId: string) {
    setPicking(null);
    setEditingId(claimId);
  }

  /** "← Back to materials" from a row's form: a row that can fold goes back
   *  onto the list as it stands; a blank one, or one over its ceiling, would
   *  block Save, so leaving it drops it rather than asking the user to guess
   *  a quantity. */
  function leaveRow(i: number) {
    if (isFoldable(evaluations[i]!.outcome)) setEditingId(null);
    else removeClaim(claims[i]!.id);
  }

  const checkoutTask = (): ICheckoutTask => ({
    task: { id: draft.id, name: draft.name, start: draft.start, end: draft.end },
    scheduleNumber,
    floorNames: stock.floorNames,
    destination,
    siteName,
    tz: stock.tz,
    t,
  });

  if (!stock.ready) {
    return <p className={styles.loading}>{t('scheduler.panel.stock.title')}…</p>;
  }

  if (picking !== null) {
    // The list is only ever opened from a rendered row, so the claim exists.
    const claim = claims.find((c) => c.id === picking.claimId)!;
    const rows = stock.rowsFor(claim.materialName);
    const unitKey = unitKeyOfLabel(claim.unit);
    const { kind } = picking;
    return (
      <StockElementPicker
        kind={kind}
        materialName={claim.materialName}
        floorIds={stock.floorIds}
        floorNames={stock.floorNames}
        floorOf={stock.floorOf}
        elements={elementsOf(rows, kind)}
        taken={stock.takenFor(claim.materialName, claim.id)}
        initialIds={chosenIds(claim.elements ?? [], kind)}
        unitKey={unitKey}
        unitLabel={claim.unit}
        onConfirm={(ids) => {
          const elements = withChosen(claim.elements ?? [], kind, ids, stock.floorOf);
          updateClaim(claim.id, { elements, quantity: claimQuantity(rows, elements, unitKey) });
          closePicker(claim.id);
        }}
        onCancel={() => closePicker(claim.id)}
      />
    );
  }

  if (reviewing) {
    const ctx = checkoutTask();
    const writes = joinWritesFor(plan.joins, ctx);
    return (
      <StockCheckoutReview
        siteId={siteId}
        plan={plan}
        onEdit={(claimId) => {
          // Back to that material's own form; the review is a list, not an editor.
          setReviewing(false);
          setEditingId(claimId);
        }}
        onRemove={(claimId) => {
          // The last one gone leaves the review with nothing to show.
          if (plan.count <= 1) setReviewing(false);
          removeClaim(claimId);
        }}
        writes={writes}
        taskStart={draft.start}
        onBeforeJoin={onBeforeJoin}
        onBack={() => setReviewing(false)}
        onContinue={() => {
          setReviewing(false);
          onCreateDelivery(buildCheckoutHandoff(plan.newLines, writes, ctx));
        }}
      />
    );
  }

  return (
    <ScrollArea className={styles.body} viewportClassName={styles.bodyContent}>
      {/* The site has no take-off at all — every CAD version converted before
          this feature shipped has none, and there is no backfill. Say so,
          rather than showing an empty material dropdown that reads as "the
          CAD holds nothing on this floor". */}
      {stock.takeoffMissing && <p className={styles.noTakeoff}>{t('scheduler.panel.stock.noTakeoff')}</p>}
      {/* The take-off exists but could not be fetched (offline, a 500). A
          neutral note in the same slot: re-converting the CAD would not help,
          so it must not borrow the copy above. */}
      {stock.takeoffFailed && <p className={styles.hint}>{t('scheduler.panel.stock.takeoffError')}</p>}

      {/* The list has no back link, so the truck sits alone at the right of
          the same row it would share with one. */}
      {!anyExpanded && truck !== null && <div className={styles.topRow}>{truck}</div>}

      {claims.map((claim, i) => {
        const evaluation = evaluations[i]!;
        const { cadChanged } = evaluation;
        // What "Set" would write: every conflicting element dropped, with the
        // quantity re-derived from what is left. Fitting (rather than
        // dropping the claim's elements wholesale) keeps the ones the CAD did
        // NOT take away, which is what the user meant. Computed
        // unconditionally — it is only READ inside the banner, and a
        // `cadChanged ? … : null` here would leave the null arm unreachable.
        const fitted = stock.fitToCad(claim);
        const folded = foldedAt(i);
        /* A form TAKES the tab body (Niv, 2026-09-16), the way the element
           list and the review do: while one row is open the folded items step
           aside, so "+ Add material" opens a page rather than a form below
           the list. */
        if (anyExpanded && folded !== null) return null;
        /* In the cart, not on the page: a material still to be ordered lives
           behind "Review order" — the page is what the task has secured
           (Niv, 2026-09-16). */
        if (folded !== null && planHas(plan, claim.id) && cadChanged === null) return null;
        const booking = stock.bookingFor(claim.materialName);
        // A gap with an order on its way says so in the list's own words.
        const planned = plan.joins.some((join) => join.claim.id === claim.id);
        const onItsWay =
          planned && isFoldable(evaluation.outcome)
            ? cartStatus({ claim, outcome: evaluation.outcome, booking, plan, lang: i18n.language, t }).text
            : null;
        return (
          <div
            key={claim.id}
            className={folded === null ? styles.claimBlock : styles.foldedBlock}
            // Only a row whose form is showing holds the cursor open. A
            // folded item expanding under the pointer would move between
            // press and release — its Edit, Remove or order link would never
            // land.
            onFocus={() => { if (folded === null) setEditingId(claim.id); }}
            onBlur={(e) => {
              // Focus moving within the same row is still editing it; only a
              // move out of the row ends that.
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                setEditingId((current) => (current === claim.id ? null : current));
              }
            }}
          >
            {folded === null && (
              <>
                {/* The one way out of a row's form — the same link the
                    element list uses (ruling 2026-09-10). */}
                <div className={styles.topRow}>
                  <BackToMaterialsButton className={styles.backToCad} onClick={() => leaveRow(i)} />
                  {truck}
                </div>
                {/* The scope line explains the form; folded items need no explaining. */}
                {i === firstExpanded && (
                  <p className={styles.scope}>
                    {stock.floorNames.length === 0 && t('scheduler.panel.stock.scopeBuilding')}
                    {stock.floorNames.length === 1 && t('scheduler.panel.stock.scope', { floor: stock.floorNames[0] })}
                    {stock.floorNames.length > 1 && t('scheduler.panel.stock.scopeFloors', { floors: stock.floorNames.join(', ') })}
                  </p>
                )}
              </>
            )}
            {cadChanged && (
              <div className={styles.banner} role="alert">
                <p>
                  {t('scheduler.panel.stock.cadChanged', {
                    floor: floorsLabel(stock.floorNames, t),
                    material: claim.materialName,
                    kind: cadChanged.kind,
                    count: cadChanged.conflicting,
                  })}
                </p>
                <div className={styles.bannerActions}>
                  <button
                    type="button"
                    // Rewriting the elements to fit means there is nothing
                    // left to "keep" over the CAD, so clear the flag too.
                    onClick={() => updateClaim(claim.id, { ...fitted, cadVersionId: stock.cadVersionId, keptOverCad: undefined })}
                  >
                    {t('scheduler.panel.stock.setTo', { qty: fitted.quantity, unit: claim.unit })}
                  </button>
                  <button
                    type="button"
                    // The deliberate "persist this over-ceiling claim" choice
                    // (spec §8) — `keptOverCad` exempts it from the ceiling
                    // check for as long as it stays stamped to THIS version;
                    // a further CAD change re-flags it (claimEvaluation.ts).
                    onClick={() => updateClaim(claim.id, { cadVersionId: stock.cadVersionId, keptOverCad: true })}
                  >
                    {t('scheduler.panel.stock.keep', { qty: claim.quantity, unit: claim.unit })}
                  </button>
                </div>
              </div>
            )}
            {folded === null ? (
              <>
                <StockClaimRow
                  claim={claim}
                  evaluation={evaluation}
                  materials={stock.materials}
                  rowsFor={stock.rowsFor}
                  taken={stock.takenFor(claim.materialName, claim.id)}
                  floorIds={stock.floorIds}
                  floorNames={stock.floorNames}
                  floorOf={stock.floorOf}
                  cadVersionId={stock.cadVersionId}
                  otherClaims={stock.otherClaimsFor(claim.materialName)}
                  stockUnitFor={stock.stockUnitFor}
                  onChange={(patch) => updateClaim(claim.id, patch)}
                  onSelect={(kind) => setPicking({ claimId: claim.id, kind })}
                />
                <StockClaimOutcome
                  claim={claim}
                  evaluation={evaluation}
                  booking={booking}
                  untracked={stock.untrackedFor(claim.materialName)}
                  onItsWay={onItsWay}
                  tz={stock.tz}
                  onOpenDelivery={onOpenDelivery}
                  onOpenStock={onOpenStock}
                />
              </>
            ) : (
              <StockCartItem
                claim={claim}
                what={bookedLabel(claim, pickedFloorNames(claim, floorNameOf, stock.floorNames), t)}
                status={cartStatus({ claim, outcome: folded, booking, plan, lang: i18n.language, t })}
                onEdit={() => setEditingId(claim.id)}
                onRemove={() => removeClaim(claim.id)}
                onOpenDelivery={onOpenDelivery}
              />
            )}
          </div>
        );
      })}

      {claims.length === 0 ? (
        /* Nothing claimed yet: the tab is otherwise blank, so the one thing
           to do is centred in it — the guided empty state the two Logistics
           panels use, with this tab's own dashed button. The first claim
           puts the list back at the top and the button under it. */
        <div className={styles.emptyWrap}>
          <EmptyStateTile
            variant="drawer"
            icon={<PackageIcon size={22} />}
            title={t('scheduler.panel.stock.empty.title')}
            body={t('scheduler.panel.stock.empty.body')}
          >
            <AddMaterialButton className={styles.addBtn} onClick={addRow} />
          </EmptyStateTile>
        </div>
      ) : (
        /* Offered whatever the list still needs — ordering waits for the
           checkout — just not while a row's form is open. */
        !anyExpanded && <AddMaterialButton className={styles.addBtnStart} onClick={addRow} />
      )}

      {/* One checkout for the whole list, once nothing is being edited. */}
      {plan.count > 0 && (!anyExpanded || openRowDone) && (
        <StockCheckoutBar
          count={plan.count}
          onReview={() => {
            // The open row goes back onto the list first — the review is about
            // the whole list, and a row left open would hide it.
            if (anyExpanded) leaveRow(firstExpanded);
            setReviewing(true);
          }}
        />
      )}
    </ScrollArea>
  );
}
